// b08 Disko — disco-neon variant (refined).
// Maximalist = visual magic + calming ambient sound. Hero keeps mirror
// ball, criss-crossing laser fans, neon-pulse type and equalizer dance.
// Heavy text panels (8-track playlist, 5 dress-code rules, synth-pop hum
// panel) have been stripped. A shared sine-pad ambient hook drives a
// small floating bottom-right music toggle (warm A minor synth pad).

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";

// ─── palette extras ────────────────────────────────────────────────────────
const CYAN = "#00E0FF";
const CHARTREUSE = "#DBFF00";
const ULTRAVIOLET = "#B23BFF";

// ─── scroll-reveal helper ──────────────────────────────────────────────────
function useReveal<T extends HTMLElement>(): [React.MutableRefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || !window.IntersectionObserver) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            return;
          }
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

function Reveal({ children }: { children: React.ReactNode }) {
  const [ref, shown] = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(18px)",
        transition: "opacity 700ms ease, transform 700ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      {children}
    </div>
  );
}

// Equalizer bar — animated at ~16fps via setInterval (height in px).
function EqualizerBar({
  color,
  reduce,
  height,
}: {
  color: string;
  reduce: boolean;
  height: number;
}) {
  return (
    <div
      style={{
        width: 14,
        height: reduce ? 40 : height,
        background: color,
        boxShadow: `0 0 6px ${color}, 0 0 14px ${color}aa`,
        transition: reduce ? "none" : "height 60ms linear",
        borderRadius: 1,
      }}
    />
  );
}

function EqualizerRow({ reduce, onPulse }: { reduce: boolean; onPulse: () => void }) {
  const COLORS = ["#FF2D7A", CYAN, CHARTREUSE, ULTRAVIOLET, "#FF2D7A", CYAN, CHARTREUSE, ULTRAVIOLET];
  const [heights, setHeights] = useState<number[]>(() => COLORS.map(() => 30));
  const pulseTriggered = useRef(false);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      setHeights(COLORS.map(() => 16 + Math.floor(Math.random() * 80)));
      if (!pulseTriggered.current) {
        pulseTriggered.current = true;
        onPulse();
      }
    }, 60); // ~16fps
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce]);

  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-end",
        justifyContent: "center",
        height: 100,
      }}
    >
      {COLORS.map((c, i) => (
        <EqualizerBar key={i} color={c} reduce={reduce} height={heights[i]} />
      ))}
    </div>
  );
}

// ─── main variant ──────────────────────────────────────────────────────────

export function DiscoNeonVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  const swooshFired = useRef(false);

  // Derive name + age from t.names (e.g. "Jasur 21 yosh" / "Jasur · 21").
  const ageMatch = t.names.match(/(\d{1,3})\s*(yosh|yil)?\s*$/i);
  const ageNumber = ageMatch ? ageMatch[1] : "21";
  const cleanName = (t.names.replace(/\s*[·•]?\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || t.names).toUpperCase();

  // Hero reveal swoosh — once per mount.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setRevealed(true);
      if (!swooshFired.current) {
        swooshFired.current = true;
        play("swoosh");
      }
    }, 200);
    return () => window.clearTimeout(id);
  }, [play]);

  // ─── Ambient calming pad — A minor warm synth ──────────────────────────
  const [music, setMusic] = useState(false);
  useAmbient(music, {
    freqs: [110.0, 164.81, 220.0, 261.63],
    type: "sine",
    master: 0.03,
    lfo: 0.1,
  });

  // Glow stacks (reused across hero / footer).
  const glowMagenta = `0 0 4px #fff, 0 0 10px ${t.accent}, 0 0 20px ${t.accent}, 0 0 38px ${t.accent}cc`;
  const glowCyan = `0 0 4px #fff, 0 0 10px ${CYAN}, 0 0 20px ${CYAN}, 0 0 38px ${CYAN}cc`;
  const glowChartreuse = `0 0 3px ${t.ink}, 0 0 8px ${CHARTREUSE}, 0 0 18px ${CHARTREUSE}cc`;

  const eyebrow: CSSProperties = {
    fontFamily: "var(--font-ui)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.4em",
    color: t.accent,
    marginBottom: 16,
    textShadow: reduce ? "none" : `0 0 8px ${t.accent}aa`,
  };
  // Touch eyebrow so the helper var is used (avoids unused warnings if trimmed).
  void eyebrow;

  // Stable laser palette for hero.
  const lasers = useMemo(() => ["#FF2D7A", CYAN, CHARTREUSE, ULTRAVIOLET, "#FF2D7A", CYAN], []);

  return (
    <div className="v-b08">
      {/* ─── Keyframes — local, scoped to this variant ────────────────── */}
      <style>{`
        @keyframes b08-pulse {
          0%, 100% { filter: brightness(0.8); }
          50% { filter: brightness(1.2); }
        }
        @keyframes b08-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes b08-spin-rev {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes b08-mb-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes b08-spot-dance {
          0%   { transform: translate(-12vw, -8vh); }
          25%  { transform: translate(18vw, -6vh); }
          50%  { transform: translate(8vw, 12vh); }
          75%  { transform: translate(-16vw, 6vh); }
          100% { transform: translate(-12vw, -8vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .b08-pulse, .b08-spin-slow, .b08-spin-rev, .b08-mb-spin, .b08-spot-dance { animation: none !important; }
        }
        [data-section-schedule-row] {
          transition: background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease;
        }
        [data-section-schedule-row]:hover {
          background-color: ${t.accent}14;
          transform: translateX(2px);
          box-shadow: inset 3px 0 0 ${CYAN};
        }
        .b08-rsvp-wrap button[data-rsvp="yes"],
        .b08-rsvp-wrap button[aria-pressed="true"] {
          box-shadow: 0 0 0 2px ${t.accent}, 0 0 18px ${t.accent}cc, 0 0 36px ${t.accent}88, 0 0 60px ${CYAN}66 !important;
        }
        .v-b08 button:focus-visible,
        .v-b08 a:focus-visible {
          outline: 2px solid ${CYAN};
          outline-offset: 3px;
        }
        @media (max-width: 768px) {
          .v-b08 .v-b08-hero {
            padding: 64px 18px 148px !important;
            min-height: auto !important;
          }
          .v-b08 .v-b08-hero h1 {
            font-size: clamp(56px, 16vw, 96px) !important;
          }
          .v-b08 .v-b08-hero-content {
            max-width: 560px !important;
          }
          .v-b08 .v-b08-hero-eyebrow {
            margin-top: 40px !important;
            font-size: 10px !important;
            letter-spacing: 0.32em !important;
          }
          /* Date/venue line: break onto multiple lines to avoid equalizer overlap */
          .v-b08 .v-b08-date-line {
            font-size: 11px !important;
            letter-spacing: 0.24em !important;
            word-break: break-word !important;
            white-space: normal !important;
            max-width: 320px !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          /* Equalizer row: always at static bottom of hero — no absolute on mobile */
          .v-b08 .v-b08-equalizer-wrap {
            position: static !important;
            margin-top: 24px !important;
          }
          .v-b08 .v-b08-section {
            padding: 64px 18px !important;
          }
          .v-b08 button,
          .v-b08 a[role="button"] {
            min-height: 44px;
          }
        }
      `}</style>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        className="hero-fill v-b08-hero"
        style={{
          minHeight: "100vh",
          padding: "120px 24px 80px",
          background: `radial-gradient(120% 80% at 50% 0%, ${ULTRAVIOLET}33 0%, ${t.bg} 55%, #000 100%)`,
          backgroundColor: t.bg,
          color: t.ink,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* MIRROR BALL spot — drifts diagonally across the page */}
        <div
          aria-hidden
          className="b08-spot-dance"
          style={{
            position: "absolute",
            left: "20%",
            top: "30%",
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${CYAN}22 0%, transparent 70%)`,
            filter: "blur(20px)",
            mixBlendMode: "screen",
            pointerEvents: "none",
            animation: reduce ? "none" : "b08-spot-dance 14s ease-in-out infinite",
          }}
        />
        <div
          aria-hidden
          className="b08-spot-dance"
          style={{
            position: "absolute",
            right: "10%",
            top: "50%",
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${t.accent}22 0%, transparent 70%)`,
            filter: "blur(24px)",
            mixBlendMode: "screen",
            pointerEvents: "none",
            animation: reduce ? "none" : "b08-spot-dance 18s ease-in-out infinite reverse",
          }}
        />

        {/* LASER BEAMS — slowly rotating fan, 40s per turn */}
        <div
          aria-hidden
          className="b08-spin-slow"
          style={{
            position: "absolute",
            left: "50%",
            top: "16%",
            width: 1,
            height: 1,
            animation: reduce ? "none" : "b08-spin-slow 40s linear infinite",
            pointerEvents: "none",
          }}
        >
          {lasers.map((c, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "180vmax",
                height: 2,
                background: `linear-gradient(90deg, ${c}cc 0%, ${c}00 100%)`,
                transformOrigin: "0 50%",
                transform: `rotate(${(i * 360) / lasers.length}deg)`,
                boxShadow: `0 0 8px ${c}, 0 0 20px ${c}aa`,
                mixBlendMode: "screen",
              }}
            />
          ))}
        </div>

        {/* Counter-rotating second laser fan, 60s/turn */}
        <div
          aria-hidden
          className="b08-spin-rev"
          style={{
            position: "absolute",
            left: "50%",
            top: "16%",
            width: 1,
            height: 1,
            animation: reduce ? "none" : "b08-spin-rev 60s linear infinite",
            pointerEvents: "none",
          }}
        >
          {[CHARTREUSE, ULTRAVIOLET, CYAN].map((c, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "180vmax",
                height: 1.5,
                background: `linear-gradient(90deg, ${c}aa 0%, ${c}00 100%)`,
                transformOrigin: "0 50%",
                transform: `rotate(${30 + (i * 360) / 3}deg)`,
                boxShadow: `0 0 6px ${c}`,
                mixBlendMode: "screen",
                opacity: 0.7,
              }}
            />
          ))}
        </div>

        {/* MIRROR BALL — top, slowly rotating */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(180px, 28vw)",
          }}
        >
          <div
            className="b08-mb-spin"
            style={{
              animation: reduce ? "none" : "b08-mb-spin 24s linear infinite",
            }}
          >
            <svg viewBox="0 0 200 200" style={{ width: "100%", display: "block" }}>
              <defs>
                <radialGradient id="b08-mb" cx="35%" cy="30%" r="70%">
                  <stop offset="0%" stopColor="#FFFFFF" />
                  <stop offset="35%" stopColor={CYAN} />
                  <stop offset="80%" stopColor={ULTRAVIOLET} />
                  <stop offset="100%" stopColor="#000" />
                </radialGradient>
                <pattern id="b08-mb-facets" width="14" height="14" patternUnits="userSpaceOnUse">
                  <rect width="14" height="14" fill="url(#b08-mb)" />
                  <path d="M0 0 L14 14 M14 0 L0 14" stroke="#000" strokeOpacity="0.35" strokeWidth="0.6" />
                </pattern>
              </defs>
              <line x1="100" y1="0" x2="100" y2="36" stroke={`${t.ink}55`} strokeWidth="1.2" />
              <circle cx="100" cy="100" r="64" fill="url(#b08-mb-facets)" />
              <ellipse cx="78" cy="78" rx="14" ry="9" fill="#FFFFFF" opacity="0.85" />
            </svg>
          </div>
        </div>

        {/* Hero content */}
        <div
          className="v-b08-hero-content"
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 880,
            padding: "0 8px",
            opacity: revealed ? 1 : 0,
            transform: revealed ? "none" : "translateY(20px)",
            transition: "opacity 700ms ease, transform 700ms cubic-bezier(.2,.8,.2,1)",
          }}
        >
          <div
            className="v-b08-hero-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginTop: 80,
              marginBottom: 16,
              textShadow: reduce ? "none" : `0 0 8px ${t.accent}aa`,
            }}
          >
            BU KECHA SENGA · TASHKENT 2026
          </div>
          <h1
            className="b08-pulse"
            aria-label={`${t.names}`}
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "clamp(64px, 14vw, 200px)",
              lineHeight: 0.9,
              letterSpacing: "-0.02em",
              color: t.ink,
              textShadow: glowMagenta,
              animation: reduce ? "none" : "b08-pulse 1s ease-in-out infinite",
              margin: 0,
            }}
          >
            {cleanName}{" "}
            <span
              className="b08-pulse"
              style={{
                color: t.ink,
                textShadow: glowCyan,
                animation: reduce ? "none" : "b08-pulse 1s ease-in-out infinite 0.2s",
                display: "inline-block",
              }}
            >
              {ageNumber}
            </span>
          </h1>
          <p
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(20px, 3vw, 32px)",
              marginTop: 16,
              color: t.ink,
              opacity: 0.92,
              textShadow: glowChartreuse,
            }}
          >
            Bu kecha senga
          </p>
          <div
            className="v-b08-date-line"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.4em",
              marginTop: 28,
              color: t.ink,
              textShadow: `0 0 6px ${CYAN}`,
            }}
          >
            {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
          </div>
        </div>

        {/* Equalizer bottom row */}
        <div
          className="v-b08-equalizer-wrap"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 24,
            zIndex: 2,
          }}
        >
          <EqualizerRow reduce={reduce} onPulse={() => play("ding")} />
        </div>
      </section>

      {/* ─── COUNTDOWN ──────────────────────────────────────────────── */}
      <Reveal>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ─── STORY (trimmed) ────────────────────────────────────────── */}
      <Reveal>
        <StorySection
          t={t}
          eyebrow={`${ageNumber} — erkinlik raqami`}
          title="Toshkent kechasi soat ikkida bo'sh emas"
          body={`${ageNumber} — bu raqam emas, eshik. Universitet do'stlari, parket o'ziniki, mirror ball aylanadi va lazerlar Toshkent osmonidagi yulduzlardek titraydi. Tashkentda soat 02:00 — bu bizning soat. Kel, raqsga tushamiz.`}
        />
      </Reveal>

      {/* ─── GALLERY ────────────────────────────────────────────────── */}
      <Reveal>
        <GallerySection t={t} />
      </Reveal>

      {/* ─── SCHEDULE — wraps native ScheduleSection with hover sound ── */}
      <Reveal>
        <div
          onMouseOver={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("[data-section-schedule-row]")) play("hover");
          }}
        >
          <ScheduleSection
            t={t}
            items={[
              ["21:00", "Darvoza ochiladi — kelganlar foto-zonada"],
              ["21:30", "Welcome koktel — neon Aperol Spritz qabul qilamiz"],
              ["22:00", "Birinchi DJ set — issinish, slow-burn"],
              ["23:00", "Surprize raqs — yopiq raqam, faqat o'zimiznikilar"],
              ["00:00", `Tort va shamlar — ${cleanName} ${ageNumber} yoshga qadam qo'yadi`],
              ["01:00", "Main DJ set — drop, lazerlar, mirror ball maksimum"],
              ["02:30", "Shot bar ochiladi — Toshkent shot'lari"],
              ["04:00", "Chiqish — quyosh chiqadi, taksilar kutadi"],
            ]}
          />
        </div>
      </Reveal>

      {/* ─── MAP ────────────────────────────────────────────────────── */}
      <Reveal>
        <MapSection t={t} isDark={isDark} addressLine={t.venue} />
      </Reveal>

      {/* ─── RSVP — chime on submit + glow halo on YES ───────────────── */}
      <Reveal>
        <div
          className="b08-rsvp-wrap"
          onClickCapture={(e) => {
            const tgt = e.target as HTMLElement;
            if (tgt.tagName === "BUTTON") play("chime");
          }}
        >
          <RsvpSection
            t={t}
            isDark={isDark}
            {...ctx.rsvp}
            slug={ctx.slug}
            label="Bu kecha klubga kel"
          />
        </div>
      </Reveal>

      {/* ─── SHARE ──────────────────────────────────────────────────── */}
      <Reveal>
        <ShareSection t={t} />
      </Reveal>

      {/* ─── FOOTER ─────────────────────────────────────────────────── */}
      <GuestFooter t={t} />

      {/* ─── Floating ambient music toggle ───────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((m) => !m)}
        accent={t.accent}
        ink={t.ink}
        style={{ bottom: 18, right: 18 }}
      />
    </div>
  );
}
